/**
 * 想定尺のカスタム秒数入力 (シナリオ設定画面の想定尺プルダウン末尾の「カスタム」)。
 *
 * 値の表現: フォームの duration state は属性ツリーの option key ("do_xxx") だが、カスタム値は
 * 「45秒」の形 (ラベル形式) でそのまま載せる。backend の _build_request_context はツリーで
 * 解決できない値を parse_duration_seconds で秒数として解釈する (クイック作成が素の秒数を
 * 送る経路と同じ) ため、新しい送信形式を増やさずに済む。generation_metadata.duration にも
 * 選択肢と同じ「45秒」のラベルが保存される。
 *
 * 範囲 (最小 4 秒・最大 300 秒) は backend scenario_generator の
 * CUSTOM_DURATION_MIN/MAX_SECONDS と同値にすること (FE は入力時にクランプ・BE は 400 で弾く)。
 */
import type { DurationOption } from './lyricsDurationGate';

export const CUSTOM_DURATION_MIN_SECONDS = 4;
export const CUSTOM_DURATION_MAX_SECONDS = 300;

/** プルダウンの「カスタム」行の value (実際の duration state には載せない表示専用の値) */
export const CUSTOM_DURATION_OPTION_VALUE = '__custom_duration__';

const CUSTOM_PATTERN = /^(\d+)秒$/;

/** 「45秒」形式のカスタム値から秒数を取り出す (形式が違えば null) */
export function parseCustomDurationValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = CUSTOM_PATTERN.exec(value.trim());
  return m ? Number(m[1]) : null;
}

/** 秒数をカスタム値 (「45秒」) に整形する */
export function formatCustomDurationValue(seconds: number): string {
  return `${seconds}秒`;
}

/** 入力値を整数に丸め、最小〜最大の範囲に収める。数値でなければ null */
export function clampCustomDurationSeconds(raw: number | string): number | null {
  const text = typeof raw === 'number' ? String(raw) : String(raw).trim();
  if (text === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n)) return null;
  return Math.min(CUSTOM_DURATION_MAX_SECONDS, Math.max(CUSTOM_DURATION_MIN_SECONDS, Math.round(n)));
}

/**
 * 現在値が選択肢に無く「N秒」形式ならカスタム値。選択肢が未読み込み (空) の間は、
 * 既定値「15秒」がカスタムに見えてしまうので false にする。
 */
export function isCustomDurationValue(options: readonly DurationOption[], value: string | null | undefined): boolean {
  if (!value || options.length === 0) return false;
  if (options.some((o) => o.value === value || o.label === value)) return false;
  const seconds = parseCustomDurationValue(value);
  return seconds != null && seconds >= CUSTOM_DURATION_MIN_SECONDS && seconds <= CUSTOM_DURATION_MAX_SECONDS;
}

/**
 * 現在値に対応する option を返す。選択肢に無いカスタム値は metadata.seconds を持つ疑似 option を
 * 合成する (歌モノ下限・モーション上限・分割生成の注記など、選択肢前提の判定にそのまま渡せる)。
 */
export function resolveDurationOption<T extends DurationOption>(
  options: readonly T[],
  value: string | null | undefined,
): T | DurationOption | undefined {
  if (!value) return undefined;
  const found = options.find((o) => o.value === value);
  if (found) return found;
  const seconds = parseCustomDurationValue(value);
  if (seconds == null) return undefined;
  return { value, label: formatCustomDurationValue(seconds), metadata: { seconds } };
}

/**
 * カスタム入力の確定値を duration state の値に変換する。既存の選択肢と同じ秒数なら
 * その option の value (key) を返し、プルダウンでその選択肢を選んだ状態に揃える。
 */
export function customSecondsToDurationValue(options: readonly DurationOption[], seconds: number): string {
  const same = options.find((o) => {
    const s = typeof o.metadata?.seconds === 'number'
      ? (o.metadata.seconds as number)
      : parseCustomDurationValue(o.label);
    return s === seconds;
  });
  return same ? same.value : formatCustomDurationValue(seconds);
}
